import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ApiEndpoint: React.FC<{
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  requestBody?: string;
  successResponse?: string;
  errorResponse?: string;
}> = ({ method, path, description, requestBody, successResponse, errorResponse }) => {
  const methodColor = {
    GET: 'success',
    POST: 'primary',
    PUT: 'info',
    DELETE: 'error',
  } as const;

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Chip label={method} color={methodColor[method]} size="small" sx={{ width: '80px' }} />
          <Typography variant="body1" sx={{ flexGrow: 1 }}><strong>{path}</strong></Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {requestBody && (
          <>
            <Typography variant="h6" gutterBottom>Request Body</Typography>
            <SyntaxHighlighter language="json" style={a11yDark} customStyle={{ borderRadius: '8px' }}>
              {requestBody}
            </SyntaxHighlighter>
          </>
        )}
        {successResponse && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Success Response (200 OK)</Typography>
            <SyntaxHighlighter language="json" style={a11yDark} customStyle={{ borderRadius: '8px' }}>
              {successResponse}
            </SyntaxHighlighter>
          </>
        )}
        {errorResponse && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Error Response (404 Not Found)</Typography>
            <SyntaxHighlighter language="json" style={a11yDark} customStyle={{ borderRadius: '8px' }}>
              {errorResponse}
            </SyntaxHighlighter>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ApiDocumentation: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        API Documentation
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Consumed APIs</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>External Services consumed by this application.</Typography>
        
        <ApiEndpoint
          method="POST"
          path="/api/v1/credit-bureau/score"
          description="Fetches credit score from a third-party credit bureau. This is a critical first step in the credit assessment process."
          requestBody={`{
  "nationalId": "1234567890123"
}`}
          successResponse={`{
  "nationalId": "1234567890123",
  "creditScore": 780,
  "reportDate": "2023-10-27T10:00:00Z"
}`}
          errorResponse={`{
  "error": "Applicant with specified National ID not found."
}`}
        />

        <ApiEndpoint
          method="POST"
          path="/api/v1/bank/statement"
          description="Retrieves bank account statement. This is used to analyze the applicant's financial health and spending patterns."
          requestBody={`{
  "accountNumber": "0123456789",
  "nationalId": "1234567890123",
  "durationMonths": 6
}`}
          successResponse={`{
  "accountNumber": "0123456789",
  "statementId": "stmt_aBcDeFgHiJkL",
  "statementUrl": "https://example-bank.com/statements/stmt_aBcDeFgHiJkL.pdf"
}`}
          errorResponse={`{
  "error": "Bank account not found or access denied."
}`}
        />

        <ApiEndpoint
          method="POST"
          path="/api/v1/mobile-money/transactions"
          description="Checks mobile money transaction statement. This provides insight into the applicant's daily financial activities."
          requestBody={`{
  "phoneNumber": "+256772123456",
  "durationMonths": 6
}`}
          successResponse={`{
  "phoneNumber": "+256772123456",
  "averageInflow": 5500,
  "averageOutflow": 4800,
  "statementUrl": "https://example-mmo.com/statements/trx_aBcDeFgHiJkL.pdf"
}`}
          errorResponse={`{
  "error": "Mobile money account not found."
}`}
        />
      </Paper>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>Exposed APIs</Typography>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Services exposed by this application.</Typography>
        <ApiEndpoint
          method="POST"
          path="/api/v1/loan-applications"
          description="Submits a new loan application to the Loan Management System. Expects `Content-Type: multipart/form-data`. A JSON payload with application details should be in a form field named `applicationData`, with files attached as separate parts."
          requestBody={`// 'applicationData' (JSON string) part of the multipart request:
{
  "customerType": "individual",
  "applicantName": "John Doe",
  "email": "john.doe@external.com",
  "phone": "1234567890",
  "nationalId": "1234567890123",
  "monthlyIncome": 60000,
  "requestedAmount": 100000
}

// --- Additional file parts ---
// bankStatement: (binary file data for bank_statement.pdf)
// addressProof: (binary file data for address_proof.jpg)`}
          successResponse={`{
  "applicationId": "app_kLMnOpQrStUv",
  "status": "pending",
  "message": "Application received and is being processed."
}`}
          errorResponse={`{
  "error": "Invalid application data provided.",
  "details": [
    "email is not a valid email address",
    "requestedAmount must be a positive number"
  ]
}`}
        />
      </Paper>
    </Box>
  );
};

export default ApiDocumentation; 